"use client";

import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { ListingRow, ListingRowSkeleton } from "./listing-row";
import type { MarketplaceJob } from "./use-cluster";

const PAGE_SIZE = 20;

type ListingListProps = {
  jobs: MarketplaceJob[];
  loading: boolean;
  /** Phase 5 wiring. */
  selectedId?: string | null;
  hoveredId?: string | null;
  onSelect?: (job: MarketplaceJob) => void;
  onHover?: (id: string | null) => void;
  /** User geolocation, threaded to each row for the distance indicator. */
  userLatitude?: number;
  userLongitude?: number;
};

export function ListingList({
  jobs,
  loading,
  selectedId,
  hoveredId,
  onSelect,
  onHover,
  userLatitude,
  userLongitude,
}: ListingListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset paging whenever the filtered slice identity changes (map panned to a
  // new bounding box → layout passes a freshly-memoized `jobs` array). `jobs`
  // isn't read inside the effect, it's the intentional trigger.
  // biome-ignore lint/correctness/useExhaustiveDependencies: jobs identity change is the trigger, not a reactive read
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [jobs]);

  const hasMore = visibleCount < jobs.length;

  const { ref: sentinelRef } = useInView({
    root: scrollEl ?? undefined,
    rootMargin: "200px",
    threshold: 0,
    skip: !(scrollEl && hasMore) || loading,
    onChange: (inView) => {
      if (inView && hasMore && !loading) {
        setVisibleCount((n) => n + PAGE_SIZE);
      }
    },
  });

  const visibleJobs = jobs.slice(0, visibleCount);

  const isLoadingInitial = loading && jobs.length === 0;
  const isEmpty = !loading && jobs.length === 0;

  // Scroll the selected row into view when selectedId changes (phase 5d).
  useEffect(() => {
    if (!selectedId) {
      return;
    }
    const row = scrollRef.current?.querySelector(`#listing-${selectedId}`);
    row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedId]);

  return (
    <div
      className="flex min-h-0 flex-1 flex-col divide-y divide-border/70 overflow-y-auto overscroll-contain"
      ref={(node) => {
        scrollRef.current = node;
        setScrollEl(node);
      }}
    >
      {isLoadingInitial && (
        <div className="divide-y divide-border/70">
          {Array.from({ length: 8 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positionally stable + unkeyed by data
            <ListingRowSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
          <p className="font-medium text-sm">No jobs on this map view</p>
          <p className="max-w-56 text-muted-foreground text-xs">
            Pan or zoom the map, or clear filters to see more listings.
          </p>
        </div>
      )}

      {!(isLoadingInitial || isEmpty) && (
        <>
          {visibleJobs.map((job) => (
            <ListingRow
              isActive={
                selectedId === job.id || hoveredId === job.id || undefined
              }
              job={job}
              key={job.id}
              onHover={onHover}
              onSelect={onSelect}
              userLatitude={userLatitude}
              userLongitude={userLongitude}
            />
          ))}
          {hasMore && (
            <div aria-hidden className="h-px w-full" ref={sentinelRef} />
          )}
          {loading && !isLoadingInitial && (
            <div className="px-3 py-2.5 text-center text-muted-foreground text-xs">
              Loading more…
            </div>
          )}
          {!hasMore && jobs.length > PAGE_SIZE && (
            <div className="px-3 py-2.5 text-center text-muted-foreground text-xs">
              End of list
            </div>
          )}
        </>
      )}
    </div>
  );
}
